/**
 * PayBreakdownTab Component
 * 
 * Displays pay calculation breakdown from backend API
 * Backend returns hours AND dollars with full line item breakdown
 */

import React from 'react';

const PayBreakdownTab = ({ payData, isLoading }) => {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  // Get values from API response
  const totalPay = payData?.total_pay || 0;
  const creditHours = payData?.total_credit_hours || 0;
  const hourlyRate = payData?.user_settings?.hourly_rate || 0;
  const breakdown = payData?.breakdown || {};
  const lineItems = payData?.line_items || [];
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-600 mb-4" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600">Loading pay calculation...</p>
        </div>
      </div>
    );
  }
  
  if (!payData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No pay data available</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Pay Summary Card */}
      <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-2">
              Total Pay (Estimated)
            </h3>
            <p className="text-4xl font-bold text-green-900">
              {formatCurrency(totalPay)}
            </p>
            <p className="text-sm text-green-700 mt-2">
              {creditHours.toFixed(2)} credit hours × {formatCurrency(hourlyRate)}/hr
            </p>
          </div>
          <span className="text-6xl text-green-600 opacity-50">💵</span>
        </div>
      </div>
      
      {/* Credit Hours Breakdown */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-xl">⏱️</span>
          Credit Hours Breakdown
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold mb-1">
              Scheduled Block
            </p>
            <p className="text-3xl font-bold text-blue-900">
              {breakdown.scheduled_block_hours?.toFixed(2) || '0.00'}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              hours
            </p>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-xs text-purple-700 uppercase tracking-wide font-semibold mb-1">
              Duty Hours
            </p>
            <p className="text-3xl font-bold text-purple-900">
              {breakdown.duty_hours?.toFixed(2) || '0.00'}
            </p>
            <p className="text-sm text-purple-700 mt-1">
              hours
            </p>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-xs text-green-700 uppercase tracking-wide font-semibold mb-1">
              Credit Hours
            </p>
            <p className="text-3xl font-bold text-green-900">
              {breakdown.credit_hours?.toFixed(2) || '0.00'}
            </p>
            <p className="text-sm text-green-700 mt-1">
              (pay basis)
            </p>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold mb-1">
              Hourly Rate
            </p>
            <p className="text-3xl font-bold text-amber-900">
              {formatCurrency(hourlyRate)}
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {payData?.user_settings?.position || 'N/A'} rate
            </p>
          </div>
        </div>
      </div>
      
      {/* Rig Comparison (if available) */}
      {payData?.rig_comparison && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Rig Comparison
          </h3>
          
          <div className="space-y-3">
            {Object.entries(payData.rig_comparison).map(([rigType, hours]) => (
              <div key={rigType} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium text-gray-700 capitalize">
                  {rigType.replace(/_/g, ' ')}
                </span>
                <span className="font-bold text-gray-900">
                  {toHours(hours)} hours
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Line Items */}
      {lineItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Pay Line Items
          </h3>
          
          <div className="space-y-2">
            {lineItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{item.description}</p>
                  {item.provenance && (
                    <p className="text-xs text-gray-500 mt-1">
                      {item.provenance}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">
                    {item.hours?.toFixed(2) || '0.00'} hrs
                  </p>
                  <p className="text-sm text-gray-600">
                    {formatCurrency(item.amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Backend Disclaimer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">📊 Pay Calculation</p>
        <p>
          Pay calculated by backend using Section 12 rules. Based on your current rate 
          ({formatCurrency(hourlyRate)}/hr) for position {payData?.user_settings?.position || 'N/A'}.
        </p>
      </div>
    </div>
  );
};

export default PayBreakdownTab;
